// Side panel: category tree + day metadata (meals, sleep, exercise dropdowns, weight, etc.)

import React from 'react';
import { CATEGORIES, CAT_BY_ID, CAT_TREE } from '../data/categories.js';
import { GENERIC_PALETTE, GENERIC_PALETTE_NAMES, FAMILY_PALETTE_NAMES, FAMILY_PALETTES } from '../data/palettes.js';

function CategoryPanel({ selectedCat, onSelectCat, onApply, onClearAll, day, customCats, addCustomCat, updateCustomCat, deleteCustomCat, catOverrides, updateCatOverride, catOrder, setCatOrder, data, onMigrateCat, collapsed: collapsedProp, onCollapsedChange }) {
  // 折叠态：若父组件传了 controlled props 就用它，否则内部 fallback state
  const [collapsedSelf, setCollapsedSelf] = React.useState(false);
  const collapsed = collapsedProp != null ? collapsedProp : collapsedSelf;
  const setCollapsed = (v) => {
    const next = typeof v === 'function' ? v(collapsed) : v;
    if (onCollapsedChange) onCollapsedChange(next);
    else setCollapsedSelf(next);
  };
  const [showAdd, setShowAdd] = React.useState(false);
  const [foldedGroups, setFoldedGroups] = React.useState({});
  const [editingId, setEditingId] = React.useState(null);
  const [dragInfo, setDragInfo] = React.useState(null); // { id, parentId }
  const [dropTarget, setDropTarget] = React.useState(null); // { id, after }

  // Build effective tree: built-in CAT_TREE merged with customCats (added as children of their parentId).
  const tree = React.useMemo(() => {
    // Map nodeId -> children ids (from CAT_TREE + custom)
    const childMap = {};
    CAT_TREE.forEach((n) => {if (n.children) childMap[n.id] = [...n.children];});
    (customCats || []).forEach((c) => {
      if (!childMap[c.parentId]) childMap[c.parentId] = [];
      childMap[c.parentId].push(c.id);
    });
    // Apply user order if present
    if (catOrder) {
      Object.keys(childMap).forEach((pid) => {
        if (catOrder[pid]) {
          const ord = catOrder[pid];
          childMap[pid].sort((a, b) => (ord.indexOf(a) === -1 ? 999 : ord.indexOf(a)) - (ord.indexOf(b) === -1 ? 999 : ord.indexOf(b)));
        }
      });
    }
    return childMap;
  }, [customCats, catOrder]);

  // count today's slots per category id (recursive sum for groups)
  const countMap = React.useMemo(() => {
    const m = {};
    if (day && day.tracks) {
      for (let t = 0; t < 3; t++) {
        const arr = day.tracks[t] || [];
        arr.forEach((c) => {if (c) m[c] = (m[c] || 0) + 1;});
      }
    }
    return m;
  }, [day && day.tracks]);
  function totalCount(catId) {
    let n = countMap[catId] || 0;
    if (tree[catId]) tree[catId].forEach((cid) => {n += totalCount(cid);});
    return n;
  }
  function lookupCat(id) {
    return CAT_BY_ID[id] || (customCats || []).find((c) => c.id === id) || CAT_TREE.find((n) => n.id === id);
  }
  function isGroup(id) {return !!tree[id] && tree[id].length > 0;}

  function toggleGroup(id) {setFoldedGroups((f) => ({ ...f, [id]: !f[id] }));}

  function getOrderForParent(pid) {
    if (pid === 'root') return rootIds;
    return tree[pid] || [];
  }
  function reorder(parentId, draggedId, targetId, after) {
    const order = getOrderForParent(parentId).slice();
    const filtered = order.filter((x) => x !== draggedId);
    let idx = filtered.indexOf(targetId);
    if (after) idx += 1;
    filtered.splice(idx, 0, draggedId);
    if (parentId === 'root') {
      setCatOrder((o) => ({ ...(o || {}), root: filtered }));
    } else {
      setCatOrder((o) => ({ ...(o || {}), [parentId]: filtered }));
    }
  }

  function renderItem(id, depth = 0, parentId = 'root') {
    const c = lookupCat(id);
    if (!c) return null;
    const grouped = isGroup(id);
    const folded = !!foldedGroups[id];
    const count = totalCount(id);
    const isDragging = dragInfo && dragInfo.id === id;
    const isDropTarget = dropTarget && dropTarget.id === id && dragInfo && dragInfo.parentId === parentId && dragInfo.id !== id;
    return (
      <React.Fragment key={id}>
        <div
          className={`cat2-row cat2-item depth-${depth} ${grouped ? 'is-group' : 'is-leaf'} ${selectedCat === id ? 'active' : ''} ${isDragging ? 'dragging' : ''} ${isDropTarget ? dropTarget.after ? 'drop-after' : 'drop-before' : ''}`}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'move';
            try {e.dataTransfer.setData('text/plain', id);} catch (err) {}
            setDragInfo({ id, parentId });
          }}
          onDragOver={(e) => {
            if (!dragInfo || dragInfo.parentId !== parentId || dragInfo.id === id) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            const rect = e.currentTarget.getBoundingClientRect();
            const after = e.clientY > rect.top + rect.height / 2;
            if (!dropTarget || dropTarget.id !== id || dropTarget.after !== after) setDropTarget({ id, after });
          }}
          onDragLeave={(e) => {
            // only clear when leaving to a non-child element
            if (!e.currentTarget.contains(e.relatedTarget)) {
              if (dropTarget && dropTarget.id === id) setDropTarget(null);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (dragInfo && dragInfo.parentId === parentId && dragInfo.id !== id) {
              const after = dropTarget ? dropTarget.after : false;
              reorder(parentId, dragInfo.id, id, after);
            }
            setDragInfo(null);
            setDropTarget(null);
          }}
          onDragEnd={() => {setDragInfo(null);setDropTarget(null);}}>

          <button className="cat2-fold" onClick={() => grouped && toggleGroup(id)} disabled={!grouped} aria-label={grouped ? '折叠/展开' : ''}>
            {grouped &&
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d={folded ? 'M6 4L11 9L6 14' : 'M4 6.5L9 11.5L14 6.5'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
          </button>
          <span
            className={`cat2-dot ${c.block === false ? 'hollow' : ''}`}
            style={c.block === false ?
            { background: 'transparent', borderColor: c.color || c.fill } :
            { background: c.color || c.fill }} />

          <div className="cat2-name-wrap">
            <button
              className="cat2-name"
              onClick={() => {onSelectCat(id);onApply && onApply(id);}}>
              {c.name || c.label}</button>
            <button
              className="cat2-edit-btn"
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onDragStart={(e) => {e.preventDefault();e.stopPropagation();}}
              draggable={false}
              onClick={(e) => {e.stopPropagation();setEditingId(id);}}
              title="编辑分类"
              aria-label="编辑分类">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M11.5 2.5a1.414 1.414 0 0 1 2 2L5 13l-3 1 1-3 8.5-8.5z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <span className="cat2-count">{count || ''}</span>
          <span className="cat2-handle" aria-hidden="true" title="拖拽改变顺序">⋮⋮</span>
        </div>
        {grouped && !folded &&
        <div className="cat2-children">
            {tree[id].map((cid) => renderItem(cid, depth + 1, id))}
          </div>
        }
      </React.Fragment>);

  }

  // root order: from catOrder.root or default
  const rootIds = React.useMemo(() => {
    const built = CAT_TREE.map((n) => n.id);
    // 已经挂在某个父节点下的 id 绝不在顶层重复渲染：eat 历史上是顶层分类，移到 fun 下后，
    // 旧 catOrder.root 里残留的 'eat' 会因 lookupCat 仍返回真值而漏过过滤、在顶层多渲染一次（重复的「吃」）。
    const childIds = new Set();
    Object.values(tree).forEach((kids) => kids.forEach((k) => childIds.add(k)));
    if (catOrder && catOrder.root) {
      const out = catOrder.root.filter((id) => !childIds.has(id) && (built.includes(id) || tree[id] || lookupCat(id)));
      built.forEach((id) => {if (!out.includes(id) && !childIds.has(id)) out.push(id);});
      return out;
    }
    return built;
  }, [catOrder, customCats, tree]);

  return (
    <div className={`cat-panel cat-panel-v2 ${collapsed ? 'collapsed' : ''}`}>
      <div className="cat-panel-head">
        <button
          className="cat-collapse-btn"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? '展开分类' : '折叠分类'}>
          {collapsed ? '›' : '‹'}</button>
        {!collapsed &&
        <React.Fragment>
            <div className="cat-panel-title">时间分类</div>
            <button className="cat-panel-add" onClick={() => setShowAdd(true)} title="新建分类">+</button>
          </React.Fragment>
        }
      </div>

      {collapsed ?
      <div className="cat-rail">
          {CATEGORIES.map((c) =>
        <button
          key={c.id}
          className={`cat-rail-item ${selectedCat === c.id ? 'active' : ''}`}
          onClick={() => {onSelectCat(c.id);onApply(c.id);}}
          title={`${c.label}（${c.key}）`}>

              <span className="cat-rail-swatch" style={{ background: c.block ? c.color : 'transparent', borderColor: c.color, color: c.block ? 'var(--swatch-text)' : c.color }}>{c.short}</span>
              <span className="cat-rail-key">{c.key}</span>
            </button>
        )}
        </div> :

      <React.Fragment>
          <div className="cat-panel-hint">点击文字设置分类 · 数字 1-7 快捷键</div>
          <div className="cat2-list">
            {rootIds.map((id) => renderItem(id, 0))}
          </div>
          <div className="cat-actions">
            <div className="legend-tooltip-wrap">
              <span className="legend-trigger" tabIndex={0}>
                <span className="legend-trigger-icon" aria-hidden="true">ⓘ</span>图例语义
              </span>
              <div className="legend-help legend-popup" role="tooltip">
                <div className="legend-row"><span className="dot" style={{ background: 'var(--c-sleep)' }}></span>紫色 = 睡眠</div>
                <div className="legend-row"><span className="dot" style={{ background: 'var(--c-prep)' }}></span>蓝色 = 预备（父）</div>
                <div className="legend-row"><span className="dot" style={{ background: 'var(--c-prep1)' }}></span>牛仔蓝 = 小能熊</div>
                <div className="legend-row"><span className="dot" style={{ background: 'var(--c-prep2)' }}></span>天蓝 = 怦然</div>
                <div className="legend-row"><span className="dot ring" style={{ borderColor: 'var(--c-exer)' }}></span>玫红字 = 运动健康</div>
                <div className="legend-row"><span className="dot" style={{ background: 'var(--c-focus)' }}></span>绿色 = 专注</div>
                <div className="legend-row"><span className="dot" style={{ background: 'var(--c-eat)' }}></span>黄色 = 娱乐·吃</div>
                <div className="legend-row"><span className="dot ring" style={{ borderColor: 'var(--c-fun)' }}></span>橙字 = 娱乐放松</div>
                <div className="legend-row"><span className="dot ring" style={{ borderColor: 'var(--c-soc)' }}></span>桦木色 = 亲友社交</div>
              </div>
            </div>
          </div>
        </React.Fragment>
      }

      {showAdd &&
      <AddCategoryModal
        onClose={() => setShowAdd(false)}
        onCreate={(payload) => {addCustomCat && addCustomCat(payload);setShowAdd(false);}} />

      }

      {editingId &&
      <EditCategoryModal
        id={editingId}
        cat={lookupCat(editingId)}
        isCustom={(customCats || []).some((c) => c.id === editingId)}
        usageCount={function () {
          let n = 0;
          if (data) {
            Object.values(data).forEach((d) => {
              if (!d.tracks) return;
              for (let t = 0; t < 3; t++) for (let i = 0; i < (d.tracks[t] || []).length; i++) if (d.tracks[t][i] === editingId) n++;
            });
          }
          return n;
        }()}
        otherCats={function () {
          const out = [];
          // 所有节点现在都在 CATEGORIES 里（CAT_TREE 只是组织结构，不再有独立的 group 节点）
          const all = [...CATEGORIES];
          all.forEach((c) => {if (c.id !== editingId && !out.find((o) => o.id === c.id)) out.push(c);});
          return out;
        }()}
        onSave={(patch) => {
          updateCatOverride && updateCatOverride(editingId, patch);
          if ((customCats || []).some((c) => c.id === editingId)) updateCustomCat && updateCustomCat(editingId, patch);
          setEditingId(null);
        }}
        onDelete={(mode, targetId) => {
          // mode: 'clear' | 'merge'
          onMigrateCat && onMigrateCat(editingId, mode, targetId);
          deleteCustomCat && deleteCustomCat(editingId);
          setEditingId(null);
        }}
        onClose={() => setEditingId(null)} />

      }
    </div>);

}

// 通用色 / 同系色 / 色名 —— 抽到 data/palettes.js 单一数据源（编辑分类 + xlsx 图例共用），见顶部 import
// 找到一个 cat 对应的家族 id（用于编辑时选哪组同系色）
function getFamilyId(cat) {
  if (!cat) return 'sleep';
  if (cat.parentId && FAMILY_PALETTES[cat.parentId]) return cat.parentId;
  // 内置 sub-leaf：从 CAT_TREE 反查父
  const parentInTree = CAT_TREE.find(n => n.children && n.children.includes(cat.id));
  if (parentInTree && FAMILY_PALETTES[parentInTree.id]) return parentInTree.id;
  // 自己就是顶层
  if (FAMILY_PALETTES[cat.id]) return cat.id;
  return 'sleep';
}

// 找该家族梯度色谱里跟传入 hex 最接近的色名（用于动态生成"X:浅紫"这种标签）
function findClosestGradientName(hex, familyId) {
  const gradient = FAMILY_PALETTES[familyId] || [];
  const names = FAMILY_PALETTE_NAMES[familyId] || [];
  if (!gradient.length || !hex) return '';
  const toRgb = (h) => {
    const m = String(h).match(/^#?([0-9A-F]{6})$/i);
    if (!m) return null;
    const v = m[1];
    return [parseInt(v.slice(0,2), 16), parseInt(v.slice(2,4), 16), parseInt(v.slice(4,6), 16)];
  };
  const t = toRgb(hex);
  if (!t) return '';
  let bestIdx = 0, bestDist = Infinity;
  gradient.forEach((g, i) => {
    const rgb = toRgb(g);
    if (!rgb) return;
    const d = (rgb[0]-t[0])**2 + (rgb[1]-t[1])**2 + (rgb[2]-t[2])**2;
    if (d < bestDist) { bestDist = d; bestIdx = i; }
  });
  return names[bestIdx] || '';
}

// 把 CSS 颜色（含 var(--c-xxx)、hex、rgb()、命名色）解析成 #RRGGBB hex
function resolveCssColor(colorStr) {
  if (!colorStr) return '#888888';
  if (/^#[0-9A-Fa-f]{6}$/.test(colorStr)) return colorStr.toUpperCase();
  // var() / rgb() / name —— 借 DOM 解析 computed style
  try {
    const tmp = document.createElement('span');
    tmp.style.color = colorStr;
    tmp.style.display = 'none';
    document.body.appendChild(tmp);
    const computed = getComputedStyle(tmp).color;
    document.body.removeChild(tmp);
    const m = computed.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (m) {
      return '#' + [+m[1], +m[2], +m[3]].map(x => x.toString(16).padStart(2, '0')).join('').toUpperCase();
    }
  } catch (e) {}
  return '#888888';
}

function EditCategoryModal({ id, cat, isCustom, usageCount, otherCats, onSave, onDelete, onClose }) {
  const [name, setName] = React.useState(cat && (cat.name || cat.label) || '');
  const [color, setColor] = React.useState(cat && cat.color || '#D8C9F0');
  // hexValue：color 解析成的实际 hex，给 native picker / 文本框用
  const [hexValue, setHexValue] = React.useState(() => resolveCssColor(cat && cat.color || '#D8C9F0'));
  const [key, setKey] = React.useState(cat && cat.key || '');
  const [block, setBlock] = React.useState(cat ? cat.block !== false : true); // 填充(true) / 线框(false)
  const [showDelete, setShowDelete] = React.useState(false);
  const [delMode, setDelMode] = React.useState('clear');
  const [delTarget, setDelTarget] = React.useState(otherCats && otherCats[0] && otherCats[0].id || '');
  const familyId = getFamilyId(cat);
  const familyGradient = FAMILY_PALETTES[familyId] || GENERIC_PALETTE;
  const familyGradientNames = FAMILY_PALETTE_NAMES[familyId] || [];
  const familyLabel = (CAT_BY_ID[familyId] && (CAT_BY_ID[familyId].label || CAT_BY_ID[familyId].name)) || familyId;

  // 同色系行动态构建：[家族父 + 该家族所有子项（树顺序）] 然后用梯度色填够 7 格
  const familyPaletteItems = React.useMemo(() => {
    const ids = [];
    if (CAT_BY_ID[familyId]) ids.push(familyId);
    const treeNode = CAT_TREE.find(n => n.id === familyId);
    if (treeNode && treeNode.children) treeNode.children.forEach(cid => { if (!ids.includes(cid)) ids.push(cid); });
    CATEGORIES.forEach(c => { if (c.parentId === familyId && !ids.includes(c.id)) ids.push(c.id); });
    const items = ids.map(cid => {
      const c = CAT_BY_ID[cid];
      if (!c) return null;
      return { color: resolveCssColor(c.color), cat: c };
    }).filter(Boolean);
    // Pad with gradient fillers
    while (items.length < 7) {
      const idx = items.length;
      items.push({
        color: familyGradient[idx] || '#888',
        cat: null,
        gradientName: familyGradientNames[idx] || '',
      });
    }
    return items.slice(0, 7);
  }, [familyId]);

  // 已用色映射（按 hex → 占用 cat 名称）
  const usedColorMap = React.useMemo(() => {
    const map = {};
    CATEGORIES.forEach(c => {
      if (!c.color) return;
      const hex = resolveCssColor(c.color);
      if (hex && !map[hex]) map[hex] = c.name || c.label || c.id;
    });
    return map;
  }, []);

  function doSave() {
    const patch = { name: name.trim(), color };
    patch.key = /^[1-9]$/.test(key) ? key : null;
    patch.block = block;
    onSave && onSave(patch);
  }
  function doDelete() {
    if (delMode === 'merge' && !delTarget) return;
    onDelete && onDelete(delMode, delTarget);
  }
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">编辑分类 · {cat && (cat.name || cat.label) || id}</div>
        <div className="modal-hint">{isCustom ? '改名 / 改色 / 改快捷键 / 删除' : '改色 / 改快捷键（系统分类不可删除）'}</div>

        {/* 名称 + ID（只读）+ 快捷键 横排 */}
        <div className="form-row">
          <label className="form-field" style={{ flex: 2 }}>
            <div className="form-field-label">名称{!isCustom && ' （系统不可改）'}</div>
            <input
              className="form-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isCustom}
            />
          </label>
          <label className="form-field" style={{ flex: 2 }}>
            <div className="form-field-label">ID（只读）</div>
            <input
              className="form-input form-input-mono"
              type="text"
              value={id}
              readOnly
              title="数据存储用的英文标识，不可编辑"
            />
          </label>
          <label className="form-field" style={{ flex: 1 }}>
            <div className="form-field-label">快捷键 (1-9)</div>
            <input
              className="form-input"
              type="text"
              value={key || ''}
              onChange={(e) => setKey(e.target.value.replace(/[^1-9]/g, '').slice(0, 1))}
              placeholder="留空不绑"
            />
            {/* B16：键被别的分类占着 → 保存时自动转绑过来，这里先说清楚 */}
            {(() => {
              if (!/^[1-9]$/.test(key || '')) return null;
              const other = CATEGORIES.find((c) => c.key === key && c.id !== (cat && cat.id));
              return other ? <div className="form-key-hint">将从「{other.name || other.label}」转绑过来</div> : null;
            })()}
          </label>
        </div>

        {/* 颜色模块 */}
        <div className="form-section">
          <div className="form-field-label">颜色</div>
          <div className="cat-palette-row-label">通用色（各家族代表色，已被占用会标注）</div>
          <div className="cat-palette cat-palette-named">
            {GENERIC_PALETTE.map((c, idx) => {
              const colorName = GENERIC_PALETTE_NAMES[idx] || '';
              const usedBy = usedColorMap[c.toUpperCase()];
              const label = usedBy ? `${usedBy}:${colorName}` : colorName;
              return (
                <div key={`gen-${c}`} className="cat-palette-cell">
                  <button
                    type="button"
                    className={`cat-palette-dot ${color === c ? 'on' : ''} ${usedBy ? 'used' : ''}`}
                    style={{ background: c }}
                    onClick={() => { setColor(c); setHexValue(c.toUpperCase()); }}
                    aria-label={c}
                    title={usedBy ? `${c} —— 已被「${usedBy}」使用` : `${c}（${colorName}）`} />
                  <div className={`cat-palette-name ${usedBy ? 'used' : ''}`}>{label}</div>
                </div>
              );
            })}
          </div>
          <div className="cat-palette-row-label">同系色（{familyLabel}：跳脱色谱，已被占用会标注）</div>
          <div className="cat-palette cat-palette-named">
            {familyGradient.map((c, idx) => {
              const colorName = familyGradientNames[idx] || '';
              const usedBy = usedColorMap[c.toUpperCase()];
              const label = usedBy ? `${usedBy}:${colorName}` : colorName;
              return (
                <div key={`fam-${idx}-${c}`} className="cat-palette-cell">
                  <button
                    type="button"
                    className={`cat-palette-dot ${color === c ? 'on' : ''} ${usedBy ? 'used' : ''}`}
                    style={{ background: c }}
                    onClick={() => { setColor(c); setHexValue(c.toUpperCase()); }}
                    aria-label={c}
                    title={usedBy ? `${c} —— 已被「${usedBy}」使用` : `${c}（${colorName}）`} />
                  <div className={`cat-palette-name ${usedBy ? 'used' : ''}`}>{label}</div>
                </div>
              );
            })}
          </div>
          <div className="cat-palette-custom">
            <span className="cat-palette-custom-label">自定义：</span>
            <input
              type="color"
              className="cat-palette-picker"
              value={hexValue}
              onChange={(e) => {
                const v = e.target.value.toUpperCase();
                setColor(v);
                setHexValue(v);
              }}
              title="点击拖拽选任意色" />
            <input
              type="text"
              className="cat-palette-hex"
              value={hexValue}
              onChange={(e) => {
                const v = e.target.value.toUpperCase();
                setHexValue(v);
                if (/^#[0-9A-F]{6}$/.test(v)) setColor(v);
              }}
              placeholder="#XXXXXX"
              maxLength={7}
              spellCheck={false} />
            <span
              className="cat-palette-preview"
              style={{ background: color }}
              title="当前色预览" />
          </div>
        </div>

        {/* 样式：填充 / 线框（block）*/}
        <div className="form-section">
          <div className="form-field-label">样式</div>
          <div className="cat-block-toggle">
            {[['填充', true], ['线框', false]].map(([label, bv]) => (
              <button
                key={label}
                type="button"
                className={`cat-block-opt ${block === bv ? 'on' : ''}`}
                onClick={() => setBlock(bv)}
                title={bv ? '填充色块' : '线框描边（更淡）'}>
                <span
                  className="cat-block-dot"
                  style={bv ? { background: color } : { background: 'transparent', borderColor: color }} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {!showDelete &&
        <div className="modal-actions">
            {isCustom && <button className="btn danger" onClick={() => setShowDelete(true)}>删除分类</button>}
            <div style={{ flex: 1 }} />
            <button className="btn" onClick={onClose}>取消</button>
            <button className="btn primary" onClick={doSave}>保存</button>
          </div>
        }

        {showDelete &&
        <div className="cat-delete-warn">
            <div className="warn-title">⚠ 确认删除「{cat && (cat.name || cat.label)}」？</div>
            <div className="warn-body">
              此分类在历史数据中出现 <b>{usageCount}</b> 次。删除后，对应时间块的处理：
              <label className="warn-radio">
                <input type="radio" name="delmode" checked={delMode === 'clear'} onChange={() => setDelMode('clear')} />
                <span>清空相关时间块（恢复未分类）</span>
              </label>
              <label className="warn-radio">
                <input type="radio" name="delmode" checked={delMode === 'merge'} onChange={() => setDelMode('merge')} />
                <span>合并到：</span>
                <select className="album-input" style={{ marginLeft: 6, width: 'auto' }} value={delTarget} onChange={(e) => setDelTarget(e.target.value)} disabled={delMode !== 'merge'}>
                  {otherCats.map((o) => <option key={o.id} value={o.id}>{o.name || o.label}</option>)}
                </select>
              </label>
            </div>
            <div className="modal-actions">
              <div style={{ flex: 1 }} />
              <button className="btn" onClick={() => setShowDelete(false)}>返回</button>
              <button className="btn danger" onClick={doDelete}>确认删除</button>
            </div>
          </div>
        }
      </div>
    </div>);

}

function AddCategoryModal({ onClose, onCreate }) {
  const [name, setName] = React.useState('');
  const [parentId, setParentId] = React.useState('sleep');
  const familyGradient = FAMILY_PALETTES[parentId] || GENERIC_PALETTE;
  const familyGradientNames = FAMILY_PALETTE_NAMES[parentId] || [];

  // 同色系行动态构建：[家族父 + 子项（树顺序）] + 梯度色填到 7 格
  const familyPaletteItems = React.useMemo(() => {
    const ids = [];
    if (CAT_BY_ID[parentId]) ids.push(parentId);
    const treeNode = CAT_TREE.find(n => n.id === parentId);
    if (treeNode && treeNode.children) treeNode.children.forEach(cid => { if (!ids.includes(cid)) ids.push(cid); });
    CATEGORIES.forEach(c => { if (c.parentId === parentId && !ids.includes(c.id)) ids.push(c.id); });
    const items = ids.map(cid => {
      const c = CAT_BY_ID[cid];
      if (!c) return null;
      return { color: resolveCssColor(c.color), cat: c };
    }).filter(Boolean);
    while (items.length < 7) {
      const idx = items.length;
      items.push({
        color: familyGradient[idx] || '#888',
        cat: null,
        gradientName: familyGradientNames[idx] || '',
      });
    }
    return items.slice(0, 7);
  }, [parentId]);

  // 已用色映射：把所有现有分类的 color 解析成 hex，便于查"某色被谁用了"
  const usedColorMap = React.useMemo(() => {
    const map = {};
    CATEGORIES.forEach(c => {
      if (!c.color) return;
      const hex = resolveCssColor(c.color);
      if (hex && !map[hex]) map[hex] = c.name || c.label || c.id;
    });
    return map;
  }, []);

  const [color, setColor] = React.useState(GENERIC_PALETTE[0]);
  const [hexValue, setHexValue] = React.useState(GENERIC_PALETTE[0]);
  // 跟新分类树对应：6 个顶层 hybrid 都可以当父
  const parents = [
  { id: 'sleep',    label: '睡眠' },
  { id: 'prep',     label: '预备' },
  { id: 'exercise', label: '运动健康' },
  { id: 'focus',    label: '专注' },
  { id: 'fun',      label: '娱乐放松' },
  { id: 'social',   label: '亲友社交' }];

  function doCreate() {
    const n = name.trim();
    if (!n) return;
    const id = `custom_${Date.now()}`;
    // Build composite label "<parent>·<name>" to match built-in convention (e.g. 预备·怦然)
    const parent = parents.find(p => p.id === parentId);
    const parentRoot = parent ? parent.label.split('·')[0] : '';
    const label = parentRoot ? `${parentRoot}·${n}` : n;
    onCreate({ id, name: n, label, color, block: true, short: n.slice(0, 1), parentId });
  }
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">新建自定义分类</div>
        <div className="modal-hint">作为某个父分类的子项，可自定义颜色（比如「睡眠 → 卧床」用更浅的紫色）</div>

        {/* 名称 + 父分类 横排 */}
        <div className="form-row">
          <label className="form-field" style={{ flex: 2 }}>
            <div className="form-field-label">名称</div>
            <input className="form-input" type="text" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="例：卧床、赖床、午休…" />
          </label>
          <label className="form-field" style={{ flex: 1 }}>
            <div className="form-field-label">父分类</div>
            <select className="form-input" value={parentId} onChange={(e) => setParentId(e.target.value)}>
              {parents.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>
        </div>

        {/* 颜色模块 */}
        <div className="form-section">
          <div className="form-field-label">颜色</div>
          <div className="cat-palette-row-label">通用色（各家族代表色，已被占用会标注）</div>
          <div className="cat-palette cat-palette-named">
            {GENERIC_PALETTE.map((c, idx) => {
              const colorName = GENERIC_PALETTE_NAMES[idx] || '';
              const usedBy = usedColorMap[c.toUpperCase()];
              const label = usedBy ? `${usedBy}:${colorName}` : colorName;
              return (
                <div key={`gen-${c}`} className="cat-palette-cell">
                  <button
                    type="button"
                    className={`cat-palette-dot ${color === c ? 'on' : ''} ${usedBy ? 'used' : ''}`}
                    style={{ background: c }}
                    onClick={() => { setColor(c); setHexValue(c.toUpperCase()); }}
                    aria-label={c}
                    title={usedBy ? `${c} —— 已被「${usedBy}」使用` : `${c}（${colorName}）`} />
                  <div className={`cat-palette-name ${usedBy ? 'used' : ''}`}>{label}</div>
                </div>
              );
            })}
          </div>
          <div className="cat-palette-row-label">同系色（{(parents.find(p => p.id === parentId) || {}).label || ''}：跳脱色谱，已被占用会标注）</div>
          <div className="cat-palette cat-palette-named">
            {familyGradient.map((c, idx) => {
              const colorName = familyGradientNames[idx] || '';
              const usedBy = usedColorMap[c.toUpperCase()];
              const label = usedBy ? `${usedBy}:${colorName}` : colorName;
              return (
                <div key={`fam-${idx}-${c}`} className="cat-palette-cell">
                  <button
                    type="button"
                    className={`cat-palette-dot ${color === c ? 'on' : ''} ${usedBy ? 'used' : ''}`}
                    style={{ background: c }}
                    onClick={() => { setColor(c); setHexValue(c.toUpperCase()); }}
                    aria-label={c}
                    title={usedBy ? `${c} —— 已被「${usedBy}」使用` : `${c}（${colorName}）`} />
                  <div className={`cat-palette-name ${usedBy ? 'used' : ''}`}>{label}</div>
                </div>
              );
            })}
          </div>
          {/* 自定义任意色：跟色板贴紧，没大间距 */}
          <div className="cat-palette-custom">
            <span className="cat-palette-custom-label">自定义：</span>
            <input
              type="color"
              className="cat-palette-picker"
              value={hexValue}
              onChange={(e) => {
                const v = e.target.value.toUpperCase();
                setColor(v);
                setHexValue(v);
              }}
              title="点击拖拽选任意色" />
            <input
              type="text"
              className="cat-palette-hex"
              value={hexValue}
              onChange={(e) => {
                const v = e.target.value.toUpperCase();
                setHexValue(v);
                if (/^#[0-9A-F]{6}$/.test(v)) setColor(v);
              }}
              placeholder="#XXXXXX"
              maxLength={7}
              spellCheck={false} />
            <span
              className="cat-palette-preview"
              style={{ background: color }}
              title="当前色预览" />
          </div>
        </div>

        <div className="modal-actions">
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn primary" onClick={doCreate} disabled={!name.trim()}>新建</button>
        </div>
      </div>
    </div>);

}

// Sleep duration options — colors per the Word doc reference (chip-style picker)
const SLEEP_OPTS = [
{ v: '3-6h', color: 'var(--sleep-3-6)', label: '3–6h', textColor: 'var(--chip-on-text)' },
{ v: '6-9h', color: 'var(--sleep-6-9)', label: '6–9h', textColor: 'var(--sleep-6-9-text)' },
{ v: '9-12h', color: 'var(--sleep-9-12)', label: '9–12h', textColor: 'var(--chip-on-text)' },
{ v: '12h+', color: 'var(--sleep-12)', label: '12h+', textColor: 'var(--chip-on-text)' }];


// Macaron color pool for newly-created options
const MACARON_POOL = [
'#C8E6C9', '#FFF4B8', '#FFC9D6', '#B5DDED',
'#DCC9F0', '#FFD8B8', '#C5E8D5', '#F0CFE4',
'#D3D8F0', '#FCE3B8'];

function pickColor(existingColors) {
  for (const c of MACARON_POOL) if (!existingColors.includes(c)) return c;
  return MACARON_POOL[Math.floor(Math.random() * MACARON_POOL.length)];
}

// Unified creatable tag-select (single or multi)
function TagSelect({ value, onChange, options, onAddOption, multi = false, placeholder = '选择…', allowCreate = true }) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const ref = React.useRef(null);
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    function h(e) {if (ref.current && !ref.current.contains(e.target)) {setOpen(false);setQ('');}}
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  React.useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const arr = multi ? Array.isArray(value) ? value : [] : value ? [value] : [];
  const isSelected = (v) => arr.includes(v);

  function selectVal(v) {
    if (multi) {
      if (v === '无') {onChange(arr.includes('无') ? [] : ['无']);return;}
      const next = arr.filter((x) => x !== '无');
      if (next.includes(v)) onChange(next.filter((x) => x !== v));else
      onChange([...next, v]);
    } else {
      onChange(value === v ? null : v);
      setOpen(false);
      setQ('');
    }
  }
  function removeTag(v, e) {
    e.stopPropagation();
    if (multi) onChange(arr.filter((x) => x !== v));else onChange(null);
  }
  function createOpt() {
    const name = q.trim();
    if (!name) return;
    if (options.find((o) => o.v === name)) {selectVal(name);setQ('');return;}
    const newOpt = { v: name, label: name, color: pickColor(options.map((o) => o.color)) };
    if (onAddOption) onAddOption(newOpt);
    if (multi) onChange([...arr.filter((x) => x !== '无'), name]);else
    {onChange(name);setOpen(false);}
    setQ('');
  }

  const filtered = options.filter((o) => !q.trim() || (o.label || o.v).toLowerCase().includes(q.trim().toLowerCase()));
  const exact = options.find((o) => o.v === q.trim() || o.label === q.trim());
  const showCreate = allowCreate && q.trim() && !exact;

  return (
    <div className="tsel" ref={ref}>
      <div className="tsel-control" onClick={() => setOpen((o) => !o)}>
        <div className="tsel-tags">
          {arr.length === 0 && <span className="tsel-placeholder">{placeholder}</span>}
          {arr.map((v) => {
            const opt = options.find((o) => o.v === v) || { v, label: v, color: 'var(--card-border)' };
            return (
              <span key={v} className="tsel-tag" style={{ background: opt.color, color: 'var(--chip-on-text)' }}>
                {opt.label || opt.v}
                <button type="button" className="tsel-tag-x" onClick={(e) => removeTag(v, e)} title="移除">×</button>
              </span>);

          })}
        </div>
        <span className="tsel-caret" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d={open ? 'M3.5 8.5L7 5L10.5 8.5' : 'M3.5 5.5L7 9L10.5 5.5'} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </div>
      {open && <div className="tsel-backdrop" onClick={() => setOpen(false)} />}
      {open &&
      <div className="tsel-menu">
          <input
          ref={inputRef}
          className="tsel-search"
          type="text"
          placeholder={allowCreate ? '可搜索或新建选项' : '搜索选项'}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {if (e.key === 'Enter') {e.preventDefault();if (showCreate) createOpt();else if (filtered[0]) selectVal(filtered[0].v);}}} />

          <div className="tsel-list">
            {filtered.map((o) => {
            const active = isSelected(o.v);
            return (
              <button key={o.v} type="button" className={`tsel-opt ${active ? 'active' : ''}`} onClick={() => selectVal(o.v)}>
                  <span className="tsel-opt-check">{active ? '✓' : ''}</span>
                  <span className="tsel-opt-pill" style={{ background: o.color, color: 'var(--chip-on-text)' }}>{o.label || o.v}</span>
                </button>);

          })}
            {showCreate &&
          <button type="button" className="tsel-opt tsel-create" onClick={createOpt}>
                <span className="tsel-opt-check">+</span>
                <span className="tsel-opt-pill tsel-create-pill">新建「{q.trim()}」</span>
              </button>
          }
            {filtered.length === 0 && !showCreate && <div className="tsel-empty">无匹配选项</div>}
          </div>
        </div>
      }
    </div>);

}

function DayMetaPanel({ day, onChange, customOpts, addCustom }) {
  function up(k, v) {onChange({ ...day, [k]: v });}
  function upMeal(meal, v) {onChange({ ...day, meals: { ...day.meals, [meal]: v } });}
  const sleepOpts = [...SLEEP_OPTS, ...(customOpts?.sleep || [])];
  const exerciseOpts = [...EXERCISE_OPTS, ...(customOpts?.exercise || [])];

  return (
    <div className="meta-panel">
      <div className="meta-col">
        <Section title="三餐">
          <div className="meal-row">
            {[['breakfast', '早', '9点前'], ['lunch', '午', '13点前'], ['dinner', '晚', '19点前']].map(([k, l, cutoff]) => {
              const isLate = day.meals[k] === 'late';
              const isUnset = day.meals[k] == null; // 从没点过（null）→ 几点前提示标红，区分于真正点成的「按时」
              return (
                <div key={k} className="meal-cell">
                  <div className="meal-label">
                    {l}
                    <span className={`meal-cutoff ${isUnset ? 'unset' : ''}`} title={`${cutoff}吃算按时，超过算拖延`}>{cutoff}</span>
                  </div>
                  <button
                    className={`meal-switch ${isLate ? 'late' : 'ontime'}`}
                    role="switch"
                    aria-checked={isLate}
                    onClick={() => upMeal(k, isLate ? 'ontime' : 'late')}
                    title={isLate ? '点击切回 按时' : '点击切到 拖延'}>
                    <span className="ms-track">
                      <span className="ms-label">{isLate ? '拖延' : '按时'}</span>
                      <span className="ms-knob" />
                    </span>
                  </button>
                </div>);
            })}
          </div>
        </Section>

        <CollapsibleSection
          title="生理周期"
          hint={(() => {
            const c = day.cycle;
            if (!c || !c.phase || c.phase === 'none') return '未记录';
            const phaseLabels = { menses: '行经', ovulation: '排卵期' };
            const parts = [phaseLabels[c.phase]];
            if (c.phase === 'menses') {
              if (c.flow) parts.push(`血量${['少', '一般', '多', '很多'][c.flow - 1] || c.flow}`);
              if (c.pain != null) parts.push(`痛${['无', '轻', '中', '重', '严重', '剧烈'][c.pain] || c.pain}`);
              if (c.symptoms && c.symptoms.length) parts.push(c.symptoms.join('·'));
            }
            return parts.join(' · ');
          })()}
          defaultOpen={!!(day.cycle && day.cycle.phase && day.cycle.phase !== 'none')}>
          <CycleEditor value={day.cycle} onChange={(v) => up('cycle', v)} />
        </CollapsibleSection>

        <Section title="主要事件">
          <textarea
            className="event-input"
            rows="1"
            value={day.mainEvent || ''}
            onChange={(e) => up('mainEvent', e.target.value)}
            placeholder="今天主要在做什么 / 一句话复盘" />
        </Section>
      </div>

      <div className="meta-col">
        <Section title="身体">
          <div className="body-grid">
            <label className="sg-cell">
              <div className="sg-label">骨量 kg</div>
              <input className="sg-input" type="number" step="0.1" value={day.boneMass ?? ''} onChange={(e) => up('boneMass', e.target.value ? +e.target.value : null)} />
            </label>
            <label className="sg-cell">
              <div className="sg-label">体重 kg</div>
              <input className="sg-input" type="number" step="0.1" value={day.weight ?? ''} onChange={(e) => up('weight', e.target.value ? +e.target.value : null)} />
            </label>
          </div>
          <div className="sg-label">运动类型</div>
          <TagSelect
            multi={true}
            value={day.exerciseTypes || []}
            onChange={(v) => up('exerciseTypes', v)}
            options={exerciseOpts}
            onAddOption={(opt) => addCustom && addCustom('exercise', opt)}
            placeholder="选择运动类型…" />
        </Section>

        <Section title="情绪" className="row emot-row">
          <div className="emot-grid">
            {['平稳', '焦虑', '内耗', '压抑', '委屈', '心累'].map((em) =>
            <button
              key={em}
              className={`emot ${day.emotions.includes(em) ? 'active' : ''}`}
              onClick={() => {
                const has = day.emotions.includes(em);
                up('emotions', has ? day.emotions.filter((x) => x !== em) : [...day.emotions, em]);
              }}>
                {em}
              </button>
            )}
          </div>
        </Section>
      </div>

      <div className="meta-col">
        <Section title="睡眠" extra={(() => {
          const tot = (day.sleepHours || 0) + (day.napHours || 0);
          return tot > 0 ? <span className="sleep-total">共计 {Number.isInteger(tot) ? tot : tot.toFixed(1)}h</span> : null;
        })()}>
          <div className="sleep-grid">
            <label className="sg-cell">
              <div className="sg-label">夜间 hour</div>
              <input className="sg-input" type="number" step="0.1" value={day.sleepHours ?? ''} onChange={(e) => {
                const v = e.target.value ? +e.target.value : null;
                const tier = v == null ? day.sleepDuration : v < 6 ? '3-6h' : v < 9 ? '6-9h' : v < 12 ? '9-12h' : '12h+';
                onChange({ ...day, sleepHours: v, sleepDuration: tier });
              }} />
            </label>
            <label className="sg-cell">
              <div className="sg-label">零星 hour</div>
              <input className="sg-input" type="number" step="0.1" value={day.napHours ?? ''} onChange={(e) => up('napHours', e.target.value ? +e.target.value : null)} />
            </label>
            <label className="sg-cell">
              <div className="sg-label">半夜醒</div>
              <input className="sg-input" type="number" min="0" value={day.nightWake ?? 0} onChange={(e) => up('nightWake', +e.target.value)} />
            </label>
            <div className="sg-cell sg-cell-full">
              <div className="sg-label">时长档</div>
              <TagSelect
                multi={false}
                value={day.sleepDuration}
                onChange={(v) => up('sleepDuration', v)}
                options={sleepOpts}
                onAddOption={(opt) => addCustom && addCustom('sleep', opt)}
                placeholder="选择时长档" />
            </div>
            <div className="sg-toggles">
              <button
                type="button"
                className={`macaron-toggle ${day.phoneBeforeSleep ? 'on phone' : ''}`}
                onClick={() => up('phoneBeforeSleep', !day.phoneBeforeSleep)}>
                <span className="mt-dot" />
                <span>睡前刷手机</span>
              </button>
              <button
                type="button"
                className={`macaron-toggle ${day.listenBeforeSleep ? 'on listen' : ''}`}
                onClick={() => up('listenBeforeSleep', !day.listenBeforeSleep)}>
                <span className="mt-dot" />
                <span>睡前听书</span>
              </button>
            </div>
          </div>
        </Section>

        <Section title="效率" className="row">
          <div className="seg">
            {[
              ['低能量', '几乎只能做轻活 / 全程拖延'],
              ['一般', '完成日常但没碰硬任务'],
              ['高效', '啃了需要深度专注的硬任务且没拖延'],
            ].map(([v, desc]) =>
            <button key={v} title={desc} className={`seg-btn ${day.efficiency === v ? 'active' : ''}`} onClick={() => up('efficiency', day.efficiency === v ? null : v)}>{v}</button>
            )}
          </div>
        </Section>
      </div>
    </div>);

}

function SleepDurationDropdown({ value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const selected = SLEEP_OPTS.find((o) => o.v === value);
  React.useEffect(() => {
    function handler(e) {if (ref.current && !ref.current.contains(e.target)) setOpen(false);}
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  return (
    <div className="sleep-dropdown" ref={ref}>
      <button
        type="button"
        className={`sd-toggle ${selected ? 'selected' : ''}`}
        style={selected ? { background: selected.color, borderColor: selected.color, color: selected.textColor || 'var(--chip-on-text)' } : {}}
        onClick={() => setOpen((o) => !o)}>

        <span className="sd-swatch" style={selected ? { background: 'rgba(255,255,255,0.4)' } : { background: 'var(--card-border)' }} />
        <span className="sd-label">{selected ? selected.label : '选择时长档'}</span>
        <span className="sd-arrow">{open ? '▴' : '▾'}</span>
      </button>
      {open &&
      <div className="sd-menu">
          {SLEEP_OPTS.map((o) =>
        <button
          key={o.v}
          type="button"
          className={`sd-opt ${value === o.v ? 'active' : ''}`}
          style={{ background: o.color, color: o.textColor || 'var(--chip-on-text)' }}
          onClick={() => {onChange(value === o.v ? null : o.v);setOpen(false);}}>

              <span className="sd-opt-label">{o.label}</span>
              {value === o.v && <span className="sd-check">✓</span>}
            </button>
        )}
          {value &&
        <button type="button" className="sd-clear" onClick={() => {onChange(null);setOpen(false);}}>
              清除选择
            </button>
        }
        </div>
      }
    </div>);

}

const EXERCISE_OPTS = [
{ v: '公园', color: 'var(--ex-park)', textColor: 'var(--chip-on-text)' },
{ v: '羽毛球', color: 'var(--ex-badm)', textColor: 'var(--chip-on-text)' },
{ v: '瑜伽', color: 'var(--ex-yoga)', textColor: 'var(--chip-on-text)' },
{ v: '无', color: 'color-mix(in oklch, var(--card-border) 40%, var(--bg))', textColor: 'var(--text-faint)' }];


function ExerciseMultiSelect({ value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    function h(e) {if (ref.current && !ref.current.contains(e.target)) setOpen(false);}
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  function toggle(v) {
    if (v === '无') {onChange(value.includes('无') ? [] : ['无']);return;}
    const next = value.filter((x) => x !== '无');
    if (next.includes(v)) onChange(next.filter((x) => x !== v));else
    onChange([...next, v]);
  }
  function removeTag(v, e) {e.stopPropagation();onChange(value.filter((x) => x !== v));}
  return (
    <div className="ex-select" ref={ref}>
      <div className="ex-control" onClick={() => setOpen((o) => !o)}>
        <div className="ex-tags">
          {value.length === 0 && <span className="ex-placeholder">选择运动类型…</span>}
          {value.map((v) => {
            const opt = EXERCISE_OPTS.find((o) => o.v === v);
            return (
              <span
                key={v}
                className="ex-tag"
                style={{ background: opt ? opt.color : 'var(--card-border)', color: opt && opt.textColor ? opt.textColor : 'var(--chip-on-text)' }}>

                {v}
                <button type="button" className="ex-tag-x" onClick={(e) => removeTag(v, e)} title="移除">×</button>
              </span>);

          })}
        </div>
        <span className="ex-caret">{open ? '▴' : '▾'}</span>
      </div>
      {open &&
      <div className="ex-menu">
          {EXERCISE_OPTS.map((o) => {
          const active = value.includes(o.v);
          return (
            <button
              key={o.v}
              type="button"
              className={`ex-opt ${active ? 'active' : ''}`}
              style={{ background: o.color, color: o.textColor || 'var(--chip-on-text)' }}
              onClick={() => toggle(o.v)}>

                <span>{o.v}</span>
                {active && <span className="ex-check">✓</span>}
              </button>);

        })}
        </div>
      }
    </div>);

}

function CollapsibleSection({ title, hint, defaultOpen, children }) {
  const [open, setOpen] = React.useState(!!defaultOpen);
  return (
    <div className="meta-section collapsible">
      <button type="button" className="meta-section-head collapsible-head" onClick={() => setOpen((o) => !o)}>
        <span className="meta-section-title">{title}</span>
        <span className="meta-section-extra">{hint}</span>
        <span className="collapsible-caret" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d={open ? 'M3.5 6L8 10.5L12.5 6' : 'M5.5 3.5L10 8L5.5 12.5'} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
      {open && <div className="collapsible-body">{children}</div>}
    </div>);

}

function CycleEditor({ value, onChange }) {
  const v = value || {};
  const PHASES = [
  { id: 'none', label: '无', color: 'var(--card-border)' },
  { id: 'menses', label: '行经', color: '#F8A8B8' },
  { id: 'ovulation', label: '排卵期', color: '#C5E2F0' }];

  const FLOW = [
  { v: 1, label: '少' }, { v: 2, label: '一般' }, { v: 3, label: '多' }, { v: 4, label: '很多' }];

  const PAIN = ['无', '轻', '中', '重', '严重', '剧烈'];
  const SYMS = ['腹痛', '腰酸', '乳房胀', '疲劳'];
  function up(k, val) {onChange({ ...v, [k]: val });}
  function toggleSym(s) {
    const arr = v.symptoms || [];
    onChange({ ...v, symptoms: arr.includes(s) ? arr.filter((x) => x !== s) : [...arr, s] });
  }
  return (
    <div className="cycle-editor">
      <div className="cycle-row">
        <div className="cycle-label">阶段</div>
        <div className="cycle-phases">
          {PHASES.map((p) =>
          <button key={p.id} type="button"
          className={`cycle-phase ${v.phase === p.id ? 'on' : ''}`}
          style={{ background: v.phase === p.id ? p.color : 'transparent', borderColor: p.color }}
          onClick={() => up('phase', v.phase === p.id ? null : p.id)}>{p.label}</button>
          )}
        </div>
      </div>
      {v.phase === 'menses' &&
      <React.Fragment>
          <div className="cycle-row">
            <div className="cycle-label">血量</div>
            <div className="cycle-flow">
              {FLOW.map((f) =>
            <button key={f.v} type="button" className={`cycle-drop ${(v.flow || 0) >= f.v ? 'on' : ''}`} onClick={() => up('flow', v.flow === f.v ? null : f.v)} title={f.label}>
                  💧
                </button>
            )}
              <span className="cycle-flow-label">{(FLOW.find((f) => f.v === v.flow) || {}).label || ''}</span>
            </div>
          </div>
          <div className="cycle-row">
            <div className="cycle-label">疼痛</div>
            <div className="cycle-pain">
              {PAIN.map((p, i) =>
            <button key={p} type="button" className={`cycle-pain-btn lvl-${i} ${v.pain === i ? 'on' : ''}`} onClick={() => up('pain', v.pain === i ? null : i)}>{p}</button>
            )}
            </div>
          </div>
          <div className="cycle-row">
            <div className="cycle-label">症状</div>
            <div className="cycle-syms">
              {SYMS.map((s) =>
            <button key={s} type="button" className={`emot ${(v.symptoms || []).includes(s) ? 'active' : ''}`} onClick={() => toggleSym(s)}>{s}</button>
            )}
            </div>
          </div>
        </React.Fragment>
      }
    </div>);

}

function Section({ title, extra, children, className = '' }) {
  return (
    <div className={`meta-section ${className}`.trim()}>
      <div className="meta-section-head">
        <div className="meta-section-title">{title}</div>
        {extra && <div className="meta-section-extra">{extra}</div>}
      </div>
      {children}
    </div>);

}

function CyclePanel({ day, onChange }) {
  function up(k, v) {onChange({ ...day, [k]: v });}
  const CYCLE_STATE = [
  { v: 'start', label: '开始' },
  { v: 'in', label: '行经中' },
  { v: 'end', label: '结束' }];

  const SYMPTOMS = ['腹痛', '腰酸', '乳房胀', '情绪低落', '头痛', '嗜睡'];
  const flow = day.bloodFlow || 0;
  const cramps = day.cramps || 0;
  const cycle = day.cycle || null;
  return (
    <div className="cycle">
      <div className="cycle-row">
        <span className="cycle-label">状态</span>
        <div className="cycle-segs">
          {CYCLE_STATE.map((s) =>
          <button
            key={s.v}
            type="button"
            className={`cycle-seg ${cycle === s.v ? 'on' : ''}`}
            onClick={() => up('cycle', cycle === s.v ? null : s.v)}>
            {s.label}</button>
          )}
          {cycle &&
          <input
            type="number"
            className="cycle-day-input"
            min="1" max="10"
            placeholder="第N天"
            value={day.cycleDay || ''}
            onChange={(e) => up('cycleDay', e.target.value ? +e.target.value : null)} />

          }
        </div>
      </div>
      <div className="cycle-row">
        <span className="cycle-label">血量</span>
        <div className="cycle-drops">
          {[1, 2, 3, 4].map((n) =>
          <button
            key={n}
            type="button"
            className={`cycle-drop ${n <= flow ? 'on' : ''}`}
            onClick={() => up('bloodFlow', flow === n ? n - 1 : n)}
            title={`${n} 滴`}
            aria-label={`血量 ${n}`}>
            💧</button>
          )}
          <span className="cycle-flow-text">{flow === 0 ? '—' : flow === 1 ? '少' : flow === 2 ? '中' : flow === 3 ? '多' : '极多'}</span>
        </div>
      </div>
      <div className="cycle-row">
        <span className="cycle-label">疼痛</span>
        <div className="cycle-pain">
          {[0, 1, 2, 3, 4, 5].map((n) =>
          <button
            key={n}
            type="button"
            className={`cycle-pain-cell lvl-${n} ${cramps === n ? 'on' : ''}`}
            onClick={() => up('cramps', n)}
            title={['无', '轻', '中', '重', '严重', '剧烈'][n]}>
            {n}</button>
          )}
        </div>
      </div>
      <div className="cycle-row symptoms">
        <span className="cycle-label">伴随</span>
        <div className="cycle-symptoms">
          {SYMPTOMS.map((s) => {
            const on = (day.cycleSymptoms || []).includes(s);
            return (
              <button
                key={s}
                type="button"
                className={`cycle-sym ${on ? 'on' : ''}`}
                onClick={() => up('cycleSymptoms', on ? (day.cycleSymptoms || []).filter((x) => x !== s) : [...(day.cycleSymptoms || []), s])}>
                {s}</button>);

          })}
        </div>
      </div>
    </div>);

}

export { CategoryPanel, DayMetaPanel, SleepDurationDropdown, ExerciseMultiSelect, TagSelect };
